import { useState } from 'react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ApiError } from '../../lib/api';

// Generic shape — both Location and Class match this.
export interface Dimension {
  id: string;
  companyId: string;
  name: string;
  code: string | null;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DimensionInput {
  name: string;
  code?: string;
  description?: string;
}

export interface DimensionUpdateInput extends Partial<DimensionInput> {
  isActive?: boolean;
}

interface DimensionHooks {
  useList: (
    companyId: string | null,
    opts: { includeInactive?: boolean },
  ) => UseQueryResult<Dimension[]>;
  useCreate: (
    companyId: string | null,
  ) => UseMutationResult<Dimension, Error, DimensionInput>;
  useUpdate: (
    companyId: string | null,
  ) => UseMutationResult<
    Dimension,
    Error,
    { id: string; input: DimensionUpdateInput }
  >;
  useDeactivate: (
    companyId: string | null,
  ) => UseMutationResult<Dimension, Error, string>;
}

interface DimensionPageProps {
  companyId: string | null;
  title: string;            // "Locations" or "Classes"
  singular: string;         // "location" or "class"
  hooks: DimensionHooks;
  hideInactiveKey: string;
}

export function DimensionPage({
  companyId,
  title,
  singular,
  hooks,
  hideInactiveKey,
}: DimensionPageProps) {
  const [hideInactive, setHideInactive] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(hideInactiveKey) !== 'false';
  });
  const [editing, setEditing] = useState<Dimension | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: rows = [], isLoading, error } = hooks.useList(companyId, {
    includeInactive: !hideInactive,
  });

  const toggleHideInactive = (next: boolean) => {
    setHideInactive(next);
    window.localStorage.setItem(hideInactiveKey, next ? 'true' : 'false');
  };

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>{title}</h1>
        <p className="muted">No active company.</p>
        <Link
          to="/companies"
          className="btn-primary"
          style={{ display: 'inline-block', textDecoration: 'none' }}
        >
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>{title}</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {rows.length} {hideInactive ? 'active' : 'total'}
          </p>
        </div>
        {!showCreate && !editing && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New {singular}
          </button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: 'inline-flex',
            gap: 6,
            alignItems: 'center',
            color: 'var(--text-muted)',
            fontSize: 14,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => toggleHideInactive(e.target.checked)}
            style={{ margin: 0 }}
          />
          Hide inactive
        </label>
      </div>

      {showCreate && (
        <DimensionForm
          companyId={companyId}
          singular={singular}
          hooks={hooks}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editing && (
        <DimensionForm
          companyId={companyId}
          singular={singular}
          hooks={hooks}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load {title.toLowerCase()}.</p>}

      {!isLoading && rows.length === 0 && !showCreate && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No {title.toLowerCase()} yet.
          </p>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            Add your first {singular}
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ width: 120 }}>Code</th>
              <th>Description</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <DimensionRow
                key={row.id}
                row={row}
                companyId={companyId}
                singular={singular}
                hooks={hooks}
                onEdit={() => setEditing(row)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DimensionRow({
  row,
  companyId,
  singular,
  hooks,
  onEdit,
}: {
  row: Dimension;
  companyId: string;
  singular: string;
  hooks: DimensionHooks;
  onEdit: () => void;
}) {
  const deactivate = hooks.useDeactivate(companyId);
  const update = hooks.useUpdate(companyId);

  return (
    <tr style={{ opacity: row.isActive ? 1 : 0.6 }}>
      <td style={{ fontWeight: 500 }}>{row.name}</td>
      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{row.code ?? '—'}</td>
      <td className="muted">{row.description ?? '—'}</td>
      <td>
        {row.isActive ? (
          <span className="tag ok">active</span>
        ) : (
          <span className="tag bad">inactive</span>
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button
          className="btn-secondary"
          onClick={onEdit}
          style={{ marginRight: 8 }}
        >
          Edit
        </button>
        {row.isActive ? (
          <button
            className="btn-secondary"
            onClick={() => {
              if (confirm(`Deactivate ${singular} "${row.name}"?`)) {
                deactivate.mutate(row.id);
              }
            }}
            disabled={deactivate.isPending}
          >
            Deactivate
          </button>
        ) : (
          <button
            className="btn-secondary"
            onClick={() => update.mutate({ id: row.id, input: { isActive: true } })}
            disabled={update.isPending}
          >
            Reactivate
          </button>
        )}
      </td>
    </tr>
  );
}

function DimensionForm({
  companyId,
  singular,
  hooks,
  existing,
  onClose,
}: {
  companyId: string;
  singular: string;
  hooks: DimensionHooks;
  existing?: Dimension;
  onClose: () => void;
}) {
  const create = hooks.useCreate(companyId);
  const update = hooks.useUpdate(companyId);
  const isEditing = !!existing;

  const [name, setName] = useState(existing?.name ?? '');
  const [code, setCode] = useState(existing?.code ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');

  const mutation = isEditing ? update : create;
  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : mutation.error?.message;

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        const input: DimensionInput = {
          name: name.trim(),
          code: code.trim() || undefined,
          description: description.trim() || undefined,
        };
        if (isEditing) {
          update.mutate(
            { id: existing.id, input },
            { onSuccess: () => onClose() },
          );
        } else {
          create.mutate(input, { onSuccess: () => onClose() });
        }
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>
        {isEditing ? `Edit "${existing.name}"` : `New ${singular}`}
      </h2>

      <div className="form-grid">
        <label htmlFor="d-name">Name *</label>
        <input
          id="d-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />

        <label htmlFor="d-code">Code</label>
        <input
          id="d-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={20}
          style={{ fontFamily: 'ui-monospace, monospace', maxWidth: 200 }}
        />

        <label htmlFor="d-desc">Description</label>
        <textarea
          id="d-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      {errorMessage && (
        <div className="field-error" style={{ marginTop: 12 }}>
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={mutation.isPending || !name.trim()}
        >
          {mutation.isPending
            ? isEditing
              ? 'Saving…'
              : 'Creating…'
            : isEditing
              ? 'Save changes'
              : `Create ${singular}`}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
